<script lang="ts">
  import Sidebar from './Sidebar.svelte';
  import TopBar from './TopBar.svelte';
  import { onMount } from 'svelte';
  import { location } from 'svelte-spa-router';
  import { userStore, isAuthenticated, auth } from '../stores/auth.store';
  import {
    refreshCharityPendingConfirmation,
    clearCharityPendingConfirmation,
  } from '../stores/charity-pending-confirmation.store';
  import {
    refreshEmployerPendingActions,
    clearEmployerPendingActions,
  } from '../stores/employer-pending-actions.store';
  import {
    refreshAdminPendingActions,
    clearAdminPendingActions,
  } from '../stores/admin-pending-actions.store';

  // Internal state  
  let pageTitle = 'Welcome Back';
  let activePage = 'dashboard';
  let charityPendingRefreshKey = '';
  let employerPendingRefreshKey = '';
  let adminPendingRefreshKey = '';

  $: {
    const role = $userStore?.user_role?.name;
    const userUuid = $userStore?.uuid ?? '';
    const path = getCleanPath($location);
    const nextKey = role === 'Charity' ? `${userUuid}:${path}` : '';

    if (nextKey && nextKey !== charityPendingRefreshKey) {
      charityPendingRefreshKey = nextKey;
      if (path !== '/charity-dashboard') {
        refreshCharityPendingConfirmation();
      }
    } else if (role !== 'Charity' && charityPendingRefreshKey) {
      charityPendingRefreshKey = '';
      clearCharityPendingConfirmation();
    }

    const employerNextKey = role === 'Employer' ? `${userUuid}:${path}` : '';
    if (employerNextKey && employerNextKey !== employerPendingRefreshKey) {
      employerPendingRefreshKey = employerNextKey;
      if (path !== '/employer-dashboard') {
        refreshEmployerPendingActions();
      }
    } else if (role !== 'Employer' && employerPendingRefreshKey) {
      employerPendingRefreshKey = '';
      clearEmployerPendingActions();
    }

    const adminNextKey = role === 'Admin' ? `${userUuid}:${path}` : '';
    if (adminNextKey && adminNextKey !== adminPendingRefreshKey) {
      adminPendingRefreshKey = adminNextKey;
      if (path !== '/admin-dashboard') {
        refreshAdminPendingActions();
      }
    } else if (role !== 'Admin' && adminPendingRefreshKey) {
      adminPendingRefreshKey = '';
      clearAdminPendingActions();
    }
  }

  // Defer layout calculations until after stylesheets are loaded
  // This prevents the "Layout was forced before the page was fully loaded" warning
  onMount(() => {
    // Use requestAnimationFrame to defer layout calculations until after styles load
    // This allows the browser to complete initial style application before we calculate layout
    requestAnimationFrame(() => {
      // Second RAF ensures styles are fully applied
      requestAnimationFrame(() => {
        // Layout calculations can now happen safely
      });
    });
  });

  // Helper function to get clean pathname without query parameters
  function getCleanPath(location: string): string {
    return location.split('?')[0];
  }

  // Update page details based on current route
  $: {
    const cleanPath = getCleanPath($location);
    
    if (cleanPath === '/dashboard') {
      activePage = 'dashboard';
      pageTitle = 'Donations';
    } else if (cleanPath === '/employer-dashboard' || cleanPath === '/charity-dashboard') {
      activePage = 'dashboard';
      pageTitle = 'Dashboard';
    } else if (cleanPath === '/donations') {
      activePage = 'donations';
      pageTitle = 'My Donation Requests';
    } else if (cleanPath === '/upcoming-payouts') {
      activePage = 'upcoming-payouts';
      pageTitle = 'Upcoming Payouts';
    } else if (cleanPath.startsWith('/upcoming-payout-detail')) {
      activePage = 'upcoming-payouts';
      pageTitle = 'Upcoming Payout Detail';
    } else if (cleanPath === '/pending-confirmation') {
      activePage = 'pending-confirmation';
      pageTitle = 'Pending Confirmation';
    } else if (cleanPath === '/payouts') {
      activePage = 'donations-received';
      pageTitle = 'Donations Received';
    } else if (cleanPath.startsWith('/donation-request')) {
      activePage = 'donation-requests';
      pageTitle = 'Donation Request';
    } else if (cleanPath.startsWith('/payout-detail')) {
      activePage = 'donations-received';
      pageTitle = 'Payout Detail';
    } else if (cleanPath === '/transactions') {
      activePage = 'transactions';
      pageTitle = 'My Transactions';
    } else if (cleanPath === '/donors') {
      activePage = 'donors';
      pageTitle = 'Donors';
    } else if (cleanPath.startsWith('/donor-detail')) {
      activePage = 'donors';
      pageTitle = 'Employer Payout';
    } else if (cleanPath.startsWith('/employer-payouts')) {
      activePage = 'donors';
      pageTitle = 'Employer Payouts';
    } else if (cleanPath === '/leave-requests') {
      activePage = 'leave-requests';
      pageTitle = 'Pending Requests';
    } else if (cleanPath === '/approved-requests') {
      activePage = 'outstanding-invoices';
      pageTitle = 'Pending Payments';
    } else if (cleanPath === '/view-leave-request') {
      activePage = 'leave-requests';
      pageTitle = 'Review Donation Request';
    } else if (cleanPath.startsWith('/select-employer')) {
      activePage = 'select-employer';
      pageTitle = 'Select Employer';
    } else if (cleanPath.startsWith('/invite-employer')) {
      activePage = 'invite-employer';
      pageTitle = 'Invite Employer';
    } else if (cleanPath.startsWith('/employees') || cleanPath.startsWith('/employer/employee-report')) {
      activePage = 'employees';
      pageTitle = 'Employees';
    } else if (cleanPath.startsWith('/employer/charities') || cleanPath.startsWith('/employer/view-charity')) {
      activePage = 'charities';
      pageTitle = 'Charities';
    } else if (cleanPath.startsWith('/charities') || cleanPath.startsWith('/view-charity')) {
      activePage = 'charities';
      pageTitle = 'Charities';
    } else if (cleanPath.startsWith('/employer/payment-history')) {
      activePage = 'payment-history';
      pageTitle = 'Completed Payments';
    } else if (cleanPath.startsWith('/employer/outstanding-invoices')) {
      activePage = 'outstanding-invoices';
      pageTitle = 'Pending Payments';
    } else if (cleanPath.startsWith('/employer/compliance')) {
      activePage = 'compliance';
      pageTitle = 'Compliance';
    } else if (cleanPath.startsWith('/payment-reports')) {
      activePage = 'payment-reports';
      pageTitle = 'Payment Reports';
    } else if (cleanPath.startsWith('/employee-reports')) {
      activePage = 'employee-reports';
      pageTitle = 'Pay Reports';
    } else if (cleanPath.startsWith('/reports')) {
      activePage = 'reports';
      pageTitle = 'Reports';
    } else if (cleanPath.startsWith('/profile')) {
      activePage = 'profile';
      pageTitle = 'Profile';
    } else if (cleanPath.startsWith('/charity-bank-account')) {
      activePage = 'charity-bank-account';
      pageTitle = 'Bank Account';
    } else if (cleanPath.startsWith('/charity-profile')) {
      activePage = 'charity-profile';
      pageTitle = 'Charity Profile';
    } else if (cleanPath === '/admin-dashboard') {
      activePage = 'admin-dashboard';
      pageTitle = 'Dashboard';
    } else if (cleanPath.startsWith('/admin/users')) {
      activePage = 'users';
      pageTitle = 'All Users';
    } else if (cleanPath.startsWith('/admin/user-profile')) {
      activePage = 'users';
      pageTitle = 'User Profile';
    } else if (cleanPath === '/admin/payments/received' || cleanPath.startsWith('/admin/payments/received/')) {
      activePage = 'pending-payouts';
      pageTitle =
        cleanPath === '/admin/payments/received'
          ? 'Ready for Payout'
          : 'Ready for Payout Detail';
    } else if (cleanPath === '/admin/payments/outgoing' || cleanPath.startsWith('/admin/payment-detail')) {
      activePage = cleanPath.startsWith('/admin/payment-detail') ? 'pending-confirmation' : 'completed-payments';
      pageTitle = cleanPath.startsWith('/admin/payment-detail')
        ? 'Payment Detail'
        : 'Completed';
    } else if (cleanPath === '/admin/payments/incoming') {
      activePage = 'incoming-payments';
      pageTitle = 'Incoming';
    } else if (cleanPath.startsWith('/admin/payments/incoming/')) {
      activePage = 'incoming-payments';
      pageTitle = 'Incoming Payment Detail';
    } else if (cleanPath.startsWith('/employer-profile')) {
      activePage = 'employer-profile';
      pageTitle = 'Employer Profile';
    } else if (cleanPath.startsWith('/help-center')) {
      activePage = 'help-center';
      pageTitle = 'Help & Center';
    } else if (cleanPath.startsWith('/settings')) {
      activePage = 'settings';
      pageTitle = 'Settings';
    }else if (cleanPath.startsWith('/my-donations')) {
      activePage = 'donations';
      pageTitle = 'My Donations';
    } else if (cleanPath.startsWith('/my-employers')) {
      activePage = 'my-employers';
      pageTitle = 'My Employers';
    } else if (cleanPath.startsWith('/select-employer')) {
      activePage = 'select-employer';
      pageTitle = 'Select Employer';
    } else if (cleanPath.startsWith('/manage-employees')) {
      activePage = 'manage-employees';
      pageTitle = 'Employees';
    } else if (cleanPath.startsWith('/payroll-deductions')) {
      activePage = 'payroll-deductions';
      pageTitle = 'Payroll Deductions';
    } else if (cleanPath.startsWith('/donation-list')) {
      activePage = 'donations';
      pageTitle = 'Donations';
    } else if (cleanPath.startsWith('/view-donation')) {
      activePage = 'donations';
      pageTitle = 'Review Donation Request';
    } else if (cleanPath === '/admin/pending-payouts') {
      activePage = 'pending-payouts';
      pageTitle = 'Ready for Payout';
    } else if (cleanPath === '/admin/pending-confirmation') {
      activePage = 'pending-confirmation';
      pageTitle = 'Pending Confirmation';
    } else if (cleanPath === '/admin/unconfirmed-bank-details') {
      activePage = 'unconfirmed-bank-details';
      pageTitle = 'Bank Accounts';
    } else if (cleanPath.startsWith('/admin/verification/charities')) {
      activePage = 'verification-charities';
      pageTitle = 'Charities — Pending Registration';
    } else if (cleanPath.startsWith('/admin/verification/employers')) {
      activePage = 'verification-employers';
      pageTitle = 'Employers — Pending Registration';
    } else if (cleanPath.startsWith('/charity-list')) {
      activePage = 'charities';
      pageTitle = 'Charities';
    } else if (cleanPath.startsWith('/employers')) {
      activePage = 'employers';
      pageTitle = 'Employers';
    } else if (cleanPath.startsWith('/employer-profile')) {
      activePage = 'employers';
      pageTitle = 'Employer Profile';
    } else if (cleanPath.startsWith('/admin/employee-profile')) {
      activePage = 'manage-employees';
      pageTitle = 'Employee Profile';
    } else if (cleanPath.startsWith('/donation-report')) {
      activePage = 'donation-report';
      pageTitle = 'Donation Reports';
    } else if (cleanPath.startsWith('/payroll-compliance-reports')) {
      activePage = 'payroll-compliance-reports';
      pageTitle = 'Payroll Compliance Reports';
    } else if (cleanPath.startsWith('/audit-log-reports')) {
      activePage = 'audit-log-reports';
      pageTitle = 'Audit Log';
    } else if (cleanPath.startsWith('/admin-profile-settings')) {
      activePage = 'admin-profile-settings';
      pageTitle = 'Employer Profile';
    } else if (cleanPath.startsWith('/donation-limit-settings')) {
      activePage = 'donation-limit-settings';
      pageTitle = 'Donation Limit';
    } else if (cleanPath.startsWith('/payroll-deductions')) {
      activePage = 'payroll-deductions';
      pageTitle = 'Pending Payroll Deductions';
    } else if (cleanPath.startsWith('/payroll-transaction-history')) {
      activePage = 'payroll-transaction-history';
      pageTitle = 'Transaction History';
    } else if (cleanPath.startsWith('/manual-payroll-entry')) {
      activePage = 'manual-payroll-entry';
      pageTitle = 'Manual Payroll Entry';
    } else if (cleanPath.startsWith('/refund-adjust-donation')) {
      activePage = 'refund-adjust-donation';
      pageTitle = 'Refund/Adjust Donation';
    }
  }

  // Mobile navigation (enabled for all roles except Admin)
  $: userRole = $userStore?.user_role?.name || 'User';
  $: mobileNavEnabled = userRole !== 'Admin';
  let mobileSidebarOpen = false;

  function toggleMobileSidebar(): void {
    if (!mobileNavEnabled) return;
    mobileSidebarOpen = !mobileSidebarOpen;
  }

  function closeMobileSidebar(): void {
    mobileSidebarOpen = false;
  }

  // Close mobile sidebar when navigating to a new page
  $: if ($location) {
    closeMobileSidebar();
  }

  function handleSidebarKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && mobileSidebarOpen) {
      closeMobileSidebar();
    }
  }

</script>

<svelte:window on:keydown={handleSidebarKeydown} />

<div
  class="layout authenticated"
  class:mobile-nav-enabled={mobileNavEnabled}
  class:admin-layout={!mobileNavEnabled}
>
  {#if mobileNavEnabled && mobileSidebarOpen}
    <button
      type="button"
      class="sidebar-backdrop"
      aria-label="Close navigation menu"
      on:click={closeMobileSidebar}
    ></button>
  {/if}

  <Sidebar {activePage} {mobileNavEnabled} bind:mobileOpen={mobileSidebarOpen} />
  <div class="content">
    <TopBar
      {pageTitle}
      user={$userStore}
      {mobileNavEnabled}
      mobileSidebarOpen={mobileSidebarOpen}
      on:toggleSidebar={toggleMobileSidebar}
    />
    <div class="page-content">
      <slot></slot>
    </div>
  </div>
</div>

<style>
  .layout {
    display: flex;
    min-height: 100vh;
    background-color: #f7fafc;
  }
  
  .layout.admin-layout {
    /* Must match .sidebar.admin-nav width in Sidebar.svelte */
    --sidebar-width: 300px;
  }

  .content {
    flex: 1;
    display: flex;
    flex-direction: column;
    max-width: calc(100vw - 250px);
    margin-left: 250px;
  }

  .layout.admin-layout .content {
    margin-left: var(--sidebar-width);
    max-width: calc(100vw - var(--sidebar-width));
  }

  /* Admin screens use the full content area — no centered max-width page containers */
  .layout.admin-layout .page-content :global([class$="-page"]),
  .layout.admin-layout .page-content :global([class$="-page-wrapper"]),
  .layout.admin-layout .page-content :global([class$="-container"]),
  .layout.admin-layout .page-content :global([class$="-management"]),
  .layout.admin-layout .page-content :global([class$="-profile"]),
  .layout.admin-layout .page-content :global(.admin-dashboard),
  .layout.admin-layout .page-content :global(.manual-payroll-entry),
  .layout.admin-layout .page-content :global(.refund-adjust-donation),
  .layout.admin-layout .page-content :global(.payroll-deductions-page),
  .layout.admin-layout .page-content :global(.payroll-transaction-history-page) {
    max-width: none;
    width: 100%;
    margin-left: 0;
    margin-right: 0;
  }
  
  .page-content {
    flex: 1;
    padding: 1.5rem 2rem;
    overflow-y: auto;
  }

  .sidebar-backdrop {
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.5);
    border: none;
    padding: 0;
    cursor: pointer;
    z-index: 999;
  }

  /* Responsive behavior for mobile (non-Admin users) */
  @media (max-width: 768px) {
    .layout.mobile-nav-enabled {
      flex-direction: column;
    }

    .layout.mobile-nav-enabled .content {
      margin-left: 0;
      max-width: 100vw;
    }

    .layout.mobile-nav-enabled .page-content {
      padding: 1rem;
      overflow-x: hidden;
      max-width: 100%;
    }

    /* Shared mobile patterns for charity, employee, and employer layouts */
    .layout.mobile-nav-enabled :global(.metrics-grid) {
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }

    .layout.mobile-nav-enabled :global(.page-header) {
      flex-direction: column;
      align-items: stretch;
      gap: 0.75rem;
    }

    .layout.mobile-nav-enabled :global(.filters-section) {
      flex-direction: column;
      width: 100%;
      gap: 0.75rem;
    }

    .layout.mobile-nav-enabled :global(.filter-item) {
      width: 100%;
      min-width: 0;
      max-width: none;
      flex: 1 1 100%;
    }

    .layout.mobile-nav-enabled :global(.search-section) {
      width: 100%;
      margin-left: 0;
      justify-content: stretch;
    }

    .layout.mobile-nav-enabled :global(.search-section button) {
      width: 100%;
    }

    .layout.mobile-nav-enabled :global(.donation-requests-container),
    .layout.mobile-nav-enabled :global(.charity-dashboard),
    .layout.mobile-nav-enabled :global(.employee-dashboard) {
      max-width: 100%;
    }

    /* Admin keeps desktop sidebar layout on mobile */
    .layout.admin-layout .content {
      margin-left: var(--sidebar-width);
      max-width: calc(100vw - var(--sidebar-width));
    }

    .layout.admin-layout .page-content {
      padding: 1rem;
    }
  }
</style> 